import React, { useEffect, useState, useRef, Fragment } from 'react';
import { ArrowLeft, Package, Barcode, CheckCircle2, AlertCircle, Truck, User, MapPin, Phone, CreditCard, Info, XCircle, ChevronRight, Scan, Clock, Globe, Smartphone } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { Order, OrderStatus, PaymentType, HistoryEvent } from './OrderQueue';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface OrderDetailsProps {
  order: Order;
  onBack: () => void;
  onUpdateStatus: (status: OrderStatus, updates?: Partial<Order>) => void;
  onUpdatePaymentMethod?: (paymentType: PaymentType) => void;
  readOnly?: boolean;
  initialShowRejectModal?: boolean;
  initialIsBulkScanning?: boolean;
  initialConfirmItem?: LineItem | null;
  initialShowPartialModal?: boolean;
  initialScanningItem?: LineItem | null;
  initialShowGlobalScanModal?: boolean;
  initialShowHoldModal?: boolean;
  initialShowOtpModal?: boolean;
}
interface LotNumber {
  number: string;
  expiryDate: string;
  stock: number;
}
interface LineItem {
  id: string;
  name: string;
  sku: string;
  barcode: string;
  image: string;
  quantity: number;
  pickedQuantity: number;
  isSerialized: boolean;
  serialNumbers: string[];
  status: 'Pending' | 'Picked' | 'Partially Picked' | 'Unavailable';
  lotNumbers?: LotNumber[];
  selectedLotNumber?: string;
  requiresPrescription?: boolean;
}
export function OrderDetails({
  order,
  onBack,
  onUpdateStatus,
  initialShowRejectModal = false,
  initialIsBulkScanning = false,
  initialConfirmItem = null,
  initialShowPartialModal = false,
  initialScanningItem = null,
  initialShowGlobalScanModal = false,
  initialShowHoldModal = false,
  initialShowOtpModal = false,
  readOnly = false,
  onUpdatePaymentMethod
}: OrderDetailsProps) {
  const [isBulkScanning, setIsBulkScanning] = useState(initialIsBulkScanning);
  const [isEditingPayment, setIsEditingPayment] = useState(false);
  const [isWaitingConfirmation, setIsWaitingConfirmation] = useState(false);
  const customerConfirmationTimerRef = useRef<number | null>(null);
  const [items, setItems] = useState<LineItem[]>([{
    id: '1',
    name: 'Panadol Extra 500mg',
    sku: 'PAN-001',
    barcode: '6281234567890',
    image: 'https://images.unsplash.com/photo-1584308666744-24d5c474f2ae?w=200&h=200&fit=crop',
    quantity: 2,
    pickedQuantity: 0,
    isSerialized: false,
    serialNumbers: [],
    status: 'Pending',
    requiresPrescription: true,
    lotNumbers: [{
      number: 'LOT-A1',
      expiryDate: '2025-12',
      stock: 50
    }, {
      number: 'LOT-A2',
      expiryDate: '2026-06',
      stock: 30
    }]
  }, {
    id: '2',
    name: 'Amoxicillin 500mg (Serialized)',
    sku: 'AMX-500',
    barcode: '6289876543210',
    image: 'https://images.unsplash.com/photo-1576602976047-174e57a47881?w=200&h=200&fit=crop',
    quantity: 1,
    pickedQuantity: 0,
    isSerialized: true,
    serialNumbers: [],
    status: 'Pending',
    requiresPrescription: true,
    lotNumbers: [{
      number: 'LOT-B1',
      expiryDate: '2025-08',
      stock: 100
    }]
  }, {
    id: '3',
    name: 'Vitamin C 1000mg Effervescent',
    sku: 'VIT-C',
    barcode: '6285554443332',
    image: 'https://images.unsplash.com/photo-1616671285442-299c27666291?w=200&h=200&fit=crop',
    quantity: 1,
    pickedQuantity: 0,
    isSerialized: false,
    serialNumbers: [],
    status: 'Pending',
    lotNumbers: [{
      number: 'LOT-C1',
      expiryDate: '2024-11',
      stock: 10
    }, {
      number: 'LOT-C2',
      expiryDate: '2025-03',
      stock: 45
    }]
  }]);
  const [scanningItem, setScanningItem] = useState<LineItem | null>(null);
  const [showGlobalScanModal, setShowGlobalScanModal] = useState(initialShowGlobalScanModal);
  const [globalScanValue, setGlobalScanValue] = useState('');
  const [manualQty, setManualQty] = useState('');
  const [tempSerial, setTempSerial] = useState('');
  const [tempLot, setTempLot] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(initialShowRejectModal);
  const [showPartialModal, setShowPartialModal] = useState(initialShowPartialModal);
  const [showHoldModal, setShowHoldModal] = useState(initialShowHoldModal);
  const [showOtpModal, setShowOtpModal] = useState(initialShowOtpModal);
  const [showRxModal, setShowRxModal] = useState(false);
  const [showReturnModal, setShowReturnModal] = useState(false);
  const [showReturnReceiptModal, setShowReturnReceiptModal] = useState(false);
  const [returnItemStatus, setReturnItemStatus] = useState<Record<string, {
    received: boolean;
    condition: 'Good' | 'Damaged' | 'Missing';
    note: string;
    missingQuantity: number;
  }>>({});
  const [returnReason, setReturnReason] = useState('');
  const [returnMethod, setReturnMethod] = useState<'Driver' | 'Customer'>('Customer');
  const [otpValue, setOtpValue] = useState('');
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  useEffect(() => {
    let interval: any;
    if (showOtpModal && resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    } else if (resendTimer === 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [showOtpModal, resendTimer]);
  const handleResendOtp = () => {
    setResendTimer(60);
    setCanResend(false);
    import('sonner').then(({
      toast
    }) => toast.success('OTP has been resent to the customer'));
  };
  const handleSendWhatsApp = () => {
    import('sonner').then(({
      toast
    }) => toast.success('OTP sent via WhatsApp successfully'));
  };
  const [rejectionReason, setRejectionReason] = useState('');
  const [holdReason, setHoldReason] = useState('');
  const [customReason, setCustomReason] = useState('');
  useEffect(() => {
    setShowRejectModal(initialShowRejectModal);
  }, [initialShowRejectModal]);
  useEffect(() => {
    setIsBulkScanning(initialIsBulkScanning);
  }, [initialIsBulkScanning]);
  useEffect(() => {
    setShowPartialModal(initialShowPartialModal);
  }, [initialShowPartialModal]);
  useEffect(() => {
    setShowGlobalScanModal(initialShowGlobalScanModal);
  }, [initialShowGlobalScanModal]);
  useEffect(() => {
    setShowHoldModal(initialShowHoldModal);
  }, [initialShowHoldModal]);
  useEffect(() => {
    setShowOtpModal(initialShowOtpModal);
  }, [initialShowOtpModal]);
  useEffect(() => {
    if (initialScanningItem) {
      setScanningItem(initialScanningItem);
      setShowGlobalScanModal(true);
      setManualQty(initialScanningItem.pickedQuantity > 0 ? initialScanningItem.pickedQuantity.toString() : initialScanningItem.quantity.toString());
    }
  }, [initialScanningItem]);
  useEffect(() => {
    return () => {
      if (customerConfirmationTimerRef.current !== null) {
        window.clearTimeout(customerConfirmationTimerRef.current);
      }
    };
  }, []);
  const handleConfirmPartial = () => {
    setShowPartialModal(false);
    setIsWaitingConfirmation(true);
    if (customerConfirmationTimerRef.current !== null) {
      window.clearTimeout(customerConfirmationTimerRef.current);
    }
    customerConfirmationTimerRef.current = window.setTimeout(() => {
      setIsWaitingConfirmation(false);
      customerConfirmationTimerRef.current = null;
      onUpdateStatus(order.isPickup ? 'Ready for Pickup' : 'Searching for Driver');
    }, 5000);
  };
  const rejectionReasons = ['Out of Stock', 'Prescription Invalid', 'Controlled Substance Restriction', 'Store Closing Soon', 'Delivery Area Not Covered', 'Other'];
  const handlePickItem = (itemId: string) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;
    setScanningItem(item);
    setShowGlobalScanModal(true);
    setManualQty(item.pickedQuantity > 0 ? item.pickedQuantity.toString() : item.quantity.toString());
    setTempLot(item.selectedLotNumber || '');
  };
  const handleGlobalScan = (input: string) => {
    const parts = input.split('*');
    const scanValue = parts[0].trim();
    const scanQty = parts.length > 1 ? parseInt(parts[1]) : 1;
    const item = items.find(i => i.sku === scanValue || i.isSerialized && scanValue.startsWith('SN-') || i.lotNumbers && i.lotNumbers.some(l => l.number === scanValue));
    if (item) {
      setScanningItem(item);
      setManualQty(item.pickedQuantity > 0 ? Math.min(item.pickedQuantity + scanQty, item.quantity).toString() : Math.min(scanQty, item.quantity).toString());
      if (item.isSerialized && scanValue.startsWith('SN-')) {
        setTempSerial(scanValue);
      }
      if (item.lotNumbers?.some(l => l.number === scanValue)) {
        setTempLot(scanValue);
      }
      import('sonner').then(({
        toast
      }) => {
        toast.success(`Item Identified: ${item.name}`);
      });
    } else {
      import('sonner').then(({
        toast
      }) => {
        toast.error(`Item ${scanValue} not found in this order`);
      });
    }
    setGlobalScanValue('');
  };
  const handleConfirmItemFulfillment = () => {
    if (!scanningItem) return;
    const qty = parseInt(manualQty) || 0;
    const finalQty = Math.min(qty, scanningItem.quantity);
    setItems(prev => prev.map(i => i.id === scanningItem.id ? {
      ...i,
      pickedQuantity: finalQty,
      selectedLotNumber: tempLot,
      serialNumbers: tempSerial && i.isSerialized && !i.serialNumbers.includes(tempSerial) ? [...i.serialNumbers, tempSerial] : i.serialNumbers,
      status: finalQty === i.quantity ? 'Picked' : finalQty > 0 ? 'Partially Picked' : 'Pending'
    } : i));
    // If we are in bulk scan mode, we stay in the scan modal but clear the current item
    if (isBulkScanning) {
      setScanningItem(null);
      setTempSerial('');
      setTempLot('');
      setManualQty('');
      // showGlobalScanModal remains true
    } else {
      setScanningItem(null);
      setShowGlobalScanModal(false);
      setTempSerial('');
      setTempLot('');
      setManualQty('');
    }
  };
  const allPicked = items.every(i => i.status === 'Picked' || i.status === 'Unavailable');
  return <div className="max-w-6xl mx-auto space-y-6 pb-20" data-id="element-33">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-34">
        <div className="flex items-center gap-4" data-id="element-35">
          <button onClick={onBack} className="p-2 hover:bg-white rounded-full transition-colors border border-transparent hover:border-slate-200" data-id="element-36">
            <ArrowLeft className="w-5 h-5 text-slate-600" data-id="element-37" />
          </button>
          <div data-id="element-38">
            <div className="flex items-center gap-3" data-id="element-39">
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-40">
                Order #{order.id}
              </h2>
              <div className={cn('px-2 py-0.5 rounded-md text-[10px] font-bold uppercase border', order.paymentType === 'COD' ? 'bg-amber-50 text-amber-700 border-amber-100' : 'bg-emerald-50 text-emerald-700 border-emerald-100')} data-id="element-41">
                {order.paymentType}
              </div>
            </div>
            <div className="flex items-center gap-2 mt-0.5" data-id="element-42">
              <p className="text-slate-500 text-xs" data-id="element-43">
                Placed on {order.createdAt}
              </p>
            </div>
          </div>
        </div>

        {!readOnly && <div className="flex items-center gap-3" data-id="element-44">
            {['Pending', 'Accepted', 'Picking', 'Searching for Driver', 'Driver Assigned', 'Ready for Pickup', 'Out for Delivery', 'Delivery Attempt Failed'].includes(order.status) && <div className="flex items-center gap-2" data-id="element-45">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-46">
                  Cancel Order
                </button>
                <button onClick={() => setShowHoldModal(true)} className="px-4 py-2 text-slate-600 font-bold text-sm hover:bg-slate-50 rounded-xl transition-colors" data-id="element-47">
                  Hold Order
                </button>
              </div>}

            {order.status === 'Held' && <div className="flex gap-3" data-id="element-48">
                <button onClick={() => setShowRejectModal(true)} className="px-4 py-2 text-red-600 font-bold text-sm hover:bg-red-50 rounded-xl transition-colors" data-id="element-49">
                  Cancel Order
                </button>
                <button onClick={() => onUpdateStatus(order.previousStatus || 'Pending')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all" data-id="element-50">
                  Resume Order
                </button>
              </div>}

            {order.status === 'Pending' && <div className="flex items-center gap-3" data-id="element-51">
                {order.requiresPrescription && <button onClick={() => setShowRxModal(true)} className="px-6 py-2 bg-purple-600 text-white font-bold rounded-xl shadow-lg shadow-purple-200 hover:bg-purple-700 transition-all flex items-center gap-2" data-id="element-52">
                    <Globe className="w-4 h-4" data-id="element-53" />
                    View Prescription
                  </button>}
                <button onClick={() => onUpdateStatus('Accepted')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all" data-id="element-54">
                  Accept Order
                </button>
              </div>}

            {order.status === 'Accepted' && <button onClick={() => onUpdateStatus('Picking')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-55">
                <Package className="w-4 h-4" data-id="element-56" />
                Start Picking
              </button>}

            {order.status === 'Picking' && <>
                {allPicked ? <button onClick={() => {
            if (order.isPickup) {
              onUpdateStatus('Ready for Pickup');
            } else {
              onUpdateStatus('Searching for Driver');
            }
          }} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all flex items-center gap-2" data-id="element-57">
                    <CheckCircle2 className="w-4 h-4" data-id="element-58" />
                    Ready to Pick
                  </button> : <button onClick={() => setShowPartialModal(true)} className="px-6 py-2 bg-amber-500 text-white font-bold rounded-xl shadow-lg shadow-amber-200 hover:bg-amber-600 transition-all flex items-center gap-2" data-id="element-59">
                    <CheckCircle2 className="w-4 h-4" data-id="element-60" />
                    Confirm Scan
                  </button>}
              </>}

            {order.status === 'Searching for Driver' && <button onClick={() => onUpdateStatus('Driver Assigned', {
          courierPhone: order.courierName ? '+966 9200 12345' : undefined,
          driverInfo: {
            name: 'Ahmed Al-Fahad',
            phone: '+966 55 000 1111',
            vehiclePlate: 'ABC 1234',
            rating: 4.8,
            avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=Ahmed'
          }
        })} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-61">
                <Truck className="w-4 h-4" data-id="element-62" />
                Simulate Driver Assignment
              </button>}

            {order.status === 'Driver Assigned' && <button onClick={() => onUpdateStatus('Out for Delivery')} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-63">
                <Truck className="w-4 h-4" data-id="element-64" />
                Driver Picked Up
              </button>}

            {order.status === 'Out for Delivery' && <div className="flex gap-3" data-id="element-65">
                <button onClick={() => onUpdateStatus('Delivery Attempt Failed')} className="px-4 py-2 text-rose-600 font-bold text-sm hover:bg-rose-50 rounded-xl transition-colors" data-id="element-66">
                  Delivery Failed
                </button>
                <button onClick={() => onUpdateStatus('Delivered')} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all" data-id="element-67">
                  Confirm Delivery
                </button>
              </div>}

            {order.status === 'Ready for Pickup' && <button onClick={() => setShowOtpModal(true)} className="px-6 py-2 bg-blue-600 text-white font-bold rounded-xl shadow-lg shadow-blue-200 hover:bg-blue-700 transition-all flex items-center gap-2" data-id="element-68">
                <User className="w-4 h-4" data-id="element-69" />
                Customer Pickup
              </button>}
          </div>}

        <div className="flex items-center gap-3" data-id="element-70">
          {order.status === 'Delivered' && <button onClick={() => setShowReturnModal(true)} className="px-6 py-2 bg-rose-600 text-white font-bold rounded-xl shadow-lg shadow-rose-200 hover:bg-rose-700 transition-all" data-id="element-71">
              Initiate Return
            </button>}

          {order.status === 'Return Requested' && <button onClick={() => onUpdateStatus('Refunded')} className="px-6 py-2 bg-slate-800 text-white font-bold rounded-xl shadow-lg shadow-slate-200 hover:bg-slate-900 transition-all" data-id="element-72">
              Complete Refund
            </button>}

          {order.status === 'Waiting Return Confirmation' && <button onClick={() => {
          // Initialize return status for items
          const initialStatus: any = {};
          items.forEach(item => {
            initialStatus[item.id] = {
              received: true,
              condition: 'Good',
              note: '',
              missingQuantity: 0
            };
          });
          setReturnItemStatus(initialStatus);
          setShowReturnReceiptModal(true);
        }} className="px-6 py-2 bg-emerald-600 text-white font-bold rounded-xl shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all" data-id="element-73">
              Confirm Item Return
            </button>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-6" data-id="element-74">
        <div className="col-span-2 space-y-6" data-id="element-75">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-76">
            <div className="px-6 py-4 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between" data-id="element-77">
              <div className="flex items-center gap-4" data-id="element-78">
                <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-79">
                  <Package className="w-4 h-4 text-blue-600" data-id="element-80" />
                  Order Items ({items.length})
                </h3>
                {order.status === 'Picking' && <button onClick={() => {
                setShowGlobalScanModal(true);
                setIsBulkScanning(true);
              }} className={cn('flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold transition-all', isBulkScanning ? 'bg-blue-600 text-white shadow-lg shadow-blue-200' : 'bg-slate-100 text-slate-600 hover:bg-slate-200')} data-id="element-81">
                    <Scan className="w-3.5 h-3.5" data-id="element-82" />
                    {isBulkScanning ? 'Bulk Scan Active' : 'Enable Bulk Scan'}
                  </button>}
              </div>
              {order.status === 'Picking' && <div className="text-xs font-bold text-blue-600 bg-blue-50 px-2 py-1 rounded" data-id="element-83">
                  {items.filter(i => i.status === 'Picked').length} /{' '}
                  {items.length} Fully Picked
                </div>}
            </div>

            <div className="divide-y divide-slate-100" data-id="element-84">
              {items.map(item => <div key={item.id} className={cn('p-6 flex items-center justify-between group transition-colors', item.status === 'Picked' && 'bg-emerald-50/30')} data-id="element-85">
                  <div className="flex gap-4" data-id="element-86">
                    <div className="relative" data-id="element-87">
                      <img src={item.image} alt={item.name} className={cn('w-16 h-16 rounded-xl object-cover border', item.status === 'Picked' ? 'border-emerald-200' : 'border-slate-200')} data-id="element-88" />
                      {item.status === 'Picked' && <div className="absolute -top-2 -right-2 bg-emerald-500 text-white rounded-full p-0.5 border-2 border-white" data-id="element-89">
                          <CheckCircle2 className="w-3.5 h-3.5" data-id="element-90" />
                        </div>}
                    </div>
                    <div data-id="element-91">
                      <p className="font-bold text-slate-900" data-id="element-92">{item.name}</p>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1" data-id="element-93">
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-94">
                          SKU:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-95">
                            {item.sku}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-96">
                          Barcode:{' '}
                          <span className="text-slate-600 font-mono" data-id="element-97">
                            {item.barcode}
                          </span>
                        </p>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-98">
                          Qty:{' '}
                          <span className="text-blue-600" data-id="element-99">
                            {item.pickedQuantity} / {item.quantity}
                          </span>
                        </p>
                        {item.isSerialized && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 text-blue-700 rounded text-[10px] font-bold uppercase border border-blue-100" data-id="element-100">
                            <Barcode className="w-3 h-3" data-id="element-101" />
                            Serialized
                          </div>}
                        {item.requiresPrescription && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-purple-50 text-purple-700 rounded text-[10px] font-bold uppercase border border-purple-100" data-id="element-102">
                            <Info className="w-3 h-3" data-id="element-103" />
                            Rx Required
                          </div>}
                        {item.selectedLotNumber && <div className="flex items-center gap-1 px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded text-[10px] font-bold uppercase border border-amber-100" data-id="element-104">
                            <Info className="w-3 h-3" data-id="element-105" />
                            Lot: {item.selectedLotNumber}
                          </div>}
                      </div>
                      {item.serialNumbers.length > 0 && <div className="flex flex-wrap gap-1 mt-2" data-id="element-106">
                          {item.serialNumbers.map(sn => <p key={sn} className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100" data-id="element-107">
                              SN: {sn}
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {order.status === 'Picking' && <button onClick={() => handlePickItem(item.id)} className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-sm font-bold hover:border-blue-500 hover:text-blue-600 transition-all flex items-center gap-2" data-id="element-108">
                      {item.isSerialized ? <Scan className="w-4 h-4" data-id="element-109" /> : <CheckCircle2 className="w-4 h-4" data-id="element-110" />}
                      {item.status === 'Picked' ? 'Edit' : item.isSerialized ? 'Scan Serial' : 'Confirm Qty'}
                    </button>}
                </div>)}
            </div>
          </div>

          {/* Branch Routing Flow */}
          {order.routingHistory && order.routingHistory.length > 0 && <div className="bg-blue-50 border border-blue-100 rounded-2xl p-6 shadow-sm" data-id="element-111">
              <h3 className="text-xs font-black text-blue-400 uppercase tracking-widest mb-4 flex items-center gap-2" data-id="element-112">
                <Globe className="w-4 h-4" data-id="element-113" />
                Branch Routing Flow
              </h3>
              <div className="flex items-center gap-4 overflow-x-auto pb-2" data-id="element-114">
                <div className="flex items-center shrink-0" data-id="element-115">
                  <div className="flex flex-col items-center gap-2" data-id="element-116">
                    <div className="w-10 h-10 rounded-full bg-white border-2 border-blue-200 flex items-center justify-center text-blue-600 shadow-sm" data-id="element-117">
                      <MapPin className="w-5 h-5" data-id="element-118" />
                    </div>
                    <div className="text-center" data-id="element-119">
                      <p className="text-xs font-bold text-slate-900" data-id="element-120">
                        Original Branch
                      </p>
                      <p className="text-[10px] text-slate-400 font-medium" data-id="element-121">
                        Innova Main
                      </p>
                    </div>
                  </div>
                </div>

                {order.routingHistory.map((route, idx) => <Fragment key={idx} data-id="element-122">
                    <div className="flex flex-col items-center" data-id="element-123">
                      <ChevronRight className="w-5 h-5 text-blue-300" data-id="element-124" />
                    </div>
                    <div className="flex items-center shrink-0" data-id="element-125">
                      <div className="flex flex-col items-center gap-2" data-id="element-126">
                        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white shadow-md shadow-blue-200" data-id="element-127">
                          <MapPin className="w-5 h-5" data-id="element-128" />
                        </div>
                        <div className="text-center" data-id="element-129">
                          <p className="text-xs font-bold text-slate-900" data-id="element-130">
                            {route.branchName}
                          </p>
                          <p className="text-[10px] text-blue-500 font-bold uppercase" data-id="element-131">
                            {route.timestamp.split(',')[1]}
                          </p>
                        </div>
                      </div>
                    </div>
                  </Fragment>)}
              </div>
            </div>}

          {/* Audit Trail */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-132">
            <h3 className="font-bold text-slate-900 mb-6 flex items-center gap-2" data-id="element-133">
              <Clock className="w-4 h-4 text-blue-600" data-id="element-134" />
              Full Order Audit Trail
            </h3>
            <div className="space-y-6 relative before:absolute before:left-[19px] before:top-2 before:bottom-2 before:w-0.5 before:bg-slate-100" data-id="element-135">
              {order.history?.map((event, idx) => <div key={event.id} className="relative pl-12" data-id="element-136">
                  <div className="absolute left-0 top-0 w-10 h-10 rounded-full bg-white border-2 border-slate-100 flex items-center justify-center z-10" data-id="element-137">
                    <div className="w-2 h-2 rounded-full bg-blue-500" data-id="element-138" />
                  </div>
                  <div className="p-4 bg-slate-50 rounded-xl border border-slate-100" data-id="element-139">
                    <div className="flex justify-between items-start" data-id="element-140">
                      <div data-id="element-141">
                        <p className="text-sm font-bold text-slate-900" data-id="element-142">
                          {event.status}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-143">
                          {event.message}
                        </p>
                      </div>
                      <p className="text-[10px] font-bold text-slate-400" data-id="element-144">
                        {event.timestamp}
                      </p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        <div className="space-y-6" data-id="element-145">
          {/* Order Source Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-146">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-147">
              <Globe className="w-4 h-4 text-blue-600" data-id="element-148" />
              Order Source
            </h3>
            <div className="grid grid-cols-2 gap-4" data-id="element-149">
              <div data-id="element-150">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-151">
                  Source
                </p>
                <div className="flex items-center gap-2" data-id="element-152">
                  {order.channel === 'Website' ? <div className="flex items-center gap-1.5 px-2 py-1 bg-indigo-50 text-indigo-700 rounded-md text-[10px] font-bold uppercase" data-id="element-153">
                      <Globe className="w-3 h-3" data-id="element-154" />
                      Website
                    </div> : <div className="flex items-center gap-1.5 px-2 py-1 bg-orange-50 text-orange-700 rounded-md text-[10px] font-bold uppercase" data-id="element-155">
                      <Smartphone className="w-3 h-3" data-id="element-156" />
                      Aggregator
                    </div>}
                </div>
              </div>
              <div data-id="element-157">
                <p className="text-[10px] font-bold text-slate-400 uppercase mb-1" data-id="element-158">
                  Source ID
                </p>
                <p className="text-sm font-bold text-slate-900" data-id="element-159">
                  {order.sourceOrderId || 'N/A'}
                </p>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-160">
            <div className="flex items-center justify-between mb-4" data-id="element-161">
              <h3 className="font-bold text-slate-900 flex items-center gap-2" data-id="element-162">
                <User className="w-4 h-4 text-blue-600" data-id="element-163" />
                Customer Profile
              </h3>
              {order.isLoyaltyCustomer && <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-[9px] font-black uppercase tracking-widest border border-amber-200" data-id="element-164">
                  Loyalty Member
                </span>}
            </div>
            <div className="space-y-4" data-id="element-165">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-slate-100" data-id="element-166">
                <div className="w-10 h-10 rounded-full bg-white border border-slate-200 flex items-center justify-center shrink-0 shadow-sm" data-id="element-167">
                  <User className="w-5 h-5 text-slate-400" data-id="element-168" />
                </div>
                <div data-id="element-169">
                  <p className="text-sm font-bold text-slate-900" data-id="element-170">
                    {order.customerName}
                  </p>
                  <p className="text-[10px] text-slate-500 font-medium uppercase tracking-wider" data-id="element-171">
                    {order.customerGender || 'N/A'} •{' '}
                    {order.customerAge || 'N/A'} Years
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-172">
                <div className="space-y-1" data-id="element-173">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-174">
                    City
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-175">
                    {order.customerCity || 'N/A'}
                  </p>
                </div>
                <div className="space-y-1" data-id="element-176">
                  <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-177">
                    District
                  </p>
                  <p className="text-xs font-bold text-slate-700" data-id="element-178">
                    {order.customerDistrict || 'N/A'}
                  </p>
                </div>
              </div>

              <div className="space-y-1 pt-2 border-t border-slate-50" data-id="element-179">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-180">
                  <MapPin className="w-3 h-3" data-id="element-181" /> Address
                </p>
                <p className="text-xs text-slate-600 leading-relaxed" data-id="element-182">
                  {order.customerAddress}
                </p>
              </div>

              <div className="space-y-1" data-id="element-183">
                <p className="text-[10px] font-bold text-slate-400 uppercase flex items-center gap-1" data-id="element-184">
                  <Phone className="w-3 h-3" data-id="element-185" /> Phone
                </p>
                <p className="text-xs font-bold text-blue-600" data-id="element-186">
                  {order.customerPhone}
                </p>
              </div>
            </div>
          </div>

          {/* Shipping & Courier Details */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-187">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-188">
              <Truck className="w-4 h-4 text-blue-600" data-id="element-189" />
              Shipping Details
            </h3>
            <div className="space-y-4" data-id="element-190">
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 space-y-3" data-id="element-191">
                <div className="flex justify-between items-center" data-id="element-192">
                  <span className="text-xs font-bold text-slate-500 uppercase" data-id="element-193">
                    Method
                  </span>
                  <span className="text-sm font-black text-blue-600 uppercase tracking-tight" data-id="element-194">
                    {order.isPickup ? 'Self Pickup' : 'Home Delivery'}
                  </span>
                </div>
                {!readOnly && !order.isPickup && ['Pending', 'Accepted', 'Picking', 'Searching for Driver'].includes(order.status) && <button onClick={() => onUpdateStatus(order.status, {
                isPickup: true,
                otp: order.otp || '5566',
                courierName: undefined,
                courierPhone: undefined,
                driverInfo: undefined
              })} className="w-full py-2.5 bg-white border border-blue-200 text-blue-700 rounded-xl text-xs font-black hover:bg-blue-50 transition-colors flex items-center justify-center gap-2" data-id="element-195">
                      <User className="w-4 h-4" data-id="element-196" />
                      Change to Self Pickup
                    </button>}
              </div>

              {!order.isPickup && <div className="space-y-4" data-id="element-197">
                  <div className="flex justify-between items-center" data-id="element-198">
                    <span className="text-xs font-bold text-slate-400 uppercase" data-id="element-199">
                      Courier
                    </span>
                    <div className="flex flex-col items-end gap-1" data-id="element-200">
                      <div className="flex items-center gap-2" data-id="element-201">
                        <div className="w-6 h-6 bg-slate-100 rounded flex items-center justify-center" data-id="element-202">
                          <Globe className="w-3 h-3 text-slate-500" data-id="element-203" />
                        </div>
                        <span className="text-sm font-bold text-slate-900" data-id="element-204">
                          {order.courierName || 'Not Assigned'}
                        </span>
                      </div>
                      {order.courierPhone && <div className="flex items-center gap-1.5 text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md" data-id="element-205">
                          <Phone className="w-3 h-3" data-id="element-206" />
                          {order.courierPhone}
                        </div>}
                    </div>
                  </div>

                  {order.driverInfo && <div className="pt-4 border-t border-slate-100" data-id="element-207">
                      <p className="text-[10px] font-bold text-slate-400 uppercase mb-3" data-id="element-208">
                        Assigned Driver
                      </p>
                      <div className="flex items-center gap-3" data-id="element-209">
                        <img src={order.driverInfo.avatar} alt={order.driverInfo.name} className="w-10 h-10 rounded-full border-2 border-white shadow-sm" data-id="element-210" />
                        <div className="flex-1" data-id="element-211">
                          <p className="text-sm font-bold text-slate-900" data-id="element-212">
                            {order.driverInfo.name}
                          </p>
                          <p className="text-[10px] text-slate-500 font-mono" data-id="element-213">
                            {order.driverInfo.vehiclePlate}
                          </p>
                        </div>
                        <button className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-214">
                          <Phone className="w-4 h-4" data-id="element-215" />
                        </button>
                      </div>
                    </div>}
                </div>}
            </div>
          </div>

          {/* Payment & Loyalty */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm" data-id="element-216">
            <h3 className="font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-217">
              <CreditCard className="w-4 h-4 text-blue-600" data-id="element-218" />
              Payment & Loyalty
            </h3>
            <div className="space-y-4" data-id="element-219">
              {/* Main Totals */}
              <div className="space-y-2" data-id="element-220">
                <div className="flex justify-between text-sm" data-id="element-221">
                  <span className="text-slate-500" data-id="element-222">Subtotal</span>
                  <span className="font-medium text-slate-900" data-id="element-223">
                    {((order.totalAmount + (order.discountAmount || 0)) / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-224">
                  <span className="text-slate-500" data-id="element-225">VAT (15%)</span>
                  <span className="font-medium text-slate-900" data-id="element-226">
                    {((order.totalAmount + (order.discountAmount || 0)) * 0.15 / 1.15).toFixed(2)}{' '}
                    SAR
                  </span>
                </div>
              </div>

              {/* Discounts & Loyalty Breakdown */}
              {(order.discountAmount || order.loyaltyPointsUsed || order.qitafAmount || order.walletAmount) && <div className="p-3 bg-emerald-50/50 rounded-xl border border-emerald-100 space-y-2" data-id="element-227">
                  {order.discountAmount && <div className="flex justify-between text-xs" data-id="element-228">
                      <div className="flex items-center gap-2" data-id="element-229">
                        <span className="text-emerald-700 font-bold uppercase" data-id="element-230">
                          Coupon
                        </span>
                        <span className="px-1.5 py-0.5 bg-white text-emerald-700 rounded text-[9px] font-black border border-emerald-200 uppercase" data-id="element-231">
                          {order.couponCode}
                        </span>
                      </div>
                      <span className="font-bold text-emerald-700" data-id="element-232">
                        -{order.discountAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.loyaltyPointsUsed && <div className="flex justify-between text-xs" data-id="element-233">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-234">
                        Loyalty Points ({order.loyaltyPointsUsed})
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-235">
                        Redeemed
                      </span>
                    </div>}
                  {order.qitafAmount && <div className="flex justify-between text-xs" data-id="element-236">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-237">
                        Qitaf Points
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-238">
                        -{order.qitafAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                  {order.walletAmount && <div className="flex justify-between text-xs" data-id="element-239">
                      <span className="text-emerald-700 font-bold uppercase" data-id="element-240">
                        Wallet Balance
                      </span>
                      <span className="font-bold text-emerald-700" data-id="element-241">
                        -{order.walletAmount.toFixed(2)} SAR
                      </span>
                    </div>}
                </div>}

              <div className="pt-3 border-t border-slate-100 flex justify-between items-center" data-id="element-242">
                <span className="font-bold text-slate-900" data-id="element-243">Grand Total</span>
                <span className="text-lg font-black text-blue-600" data-id="element-244">
                  {order.totalAmount.toFixed(2)} SAR
                </span>
              </div>

              {/* Payment Method Selector */}
              <div className="mt-4 space-y-3" data-id="element-245">
                <div className={cn('p-3 rounded-xl flex items-center justify-between border', order.paymentType === 'COD' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100')} data-id="element-246">
                  <div className="flex items-center gap-2" data-id="element-247">
                    <Info className={cn('w-4 h-4', order.paymentType === 'COD' ? 'text-amber-600' : 'text-blue-600')} data-id="element-248" />
                    <div className="flex flex-col" data-id="element-249">
                      <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter" data-id="element-250">
                        Payment Method
                      </span>
                      <span className={cn('text-xs font-black uppercase', order.paymentType === 'COD' ? 'text-amber-700' : 'text-blue-700')} data-id="element-251">
                        {order.paymentType}
                      </span>
                    </div>
                  </div>
                  {!readOnly && !['Out for Delivery', 'Delivered', 'Delivery Attempt Failed'].includes(order.status) && <button onClick={() => setIsEditingPayment(!isEditingPayment)} className="px-3 py-1 bg-white border border-slate-200 rounded-lg text-[10px] font-black text-blue-600 hover:border-blue-500 uppercase shadow-sm transition-all" data-id="element-252">
                        {isEditingPayment ? 'Cancel' : 'Change'}
                      </button>}
                </div>

                {isEditingPayment && <div className="grid grid-cols-2 gap-2 p-2 bg-slate-50 rounded-xl border border-slate-100 animate-in fade-in slide-in-from-top-1" data-id="element-253">
                    {['COD', 'Visa', 'Mada', 'ApplePay', 'Tamara', 'Tabby'].map(method => <button key={method} onClick={() => {
                  onUpdatePaymentMethod?.(method as PaymentType);
                  setIsEditingPayment(false);
                }} className={cn('px-3 py-2 rounded-lg text-[10px] font-bold uppercase transition-all', order.paymentType === method ? 'bg-blue-600 text-white shadow-sm' : 'bg-white border border-slate-200 text-slate-600 hover:border-blue-300')} data-id="element-254">
                          {method}
                        </button>)}
                  </div>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <AnimatePresence data-id="element-255">
        {showGlobalScanModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-256">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-257">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-258">
                <h3 className="font-bold text-slate-900" data-id="element-259">
                  {isBulkScanning ? 'Continuous Picking' : scanningItem ? 'Confirm Quantity' : 'Scan Item'}
                </h3>
                <button onClick={() => {
              setShowGlobalScanModal(false);
              setIsBulkScanning(false);
              setScanningItem(null);
            }} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-260">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-261" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-262">
                {scanningItem ? <div className="space-y-6" data-id="element-263">
                    <div className="flex flex-col items-center text-center space-y-3" data-id="element-264">
                      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-600" data-id="element-265">
                        <Package className="w-8 h-8" data-id="element-266" />
                      </div>
                      <div data-id="element-267">
                        <h4 className="font-bold text-xl text-slate-900" data-id="element-268">
                          {scanningItem.name}
                        </h4>
                        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-269">
                          SKU: {scanningItem.sku}
                        </p>
                      </div>
                      <div className="flex items-center gap-8 pt-2" data-id="element-270">
                        <div className="text-center" data-id="element-271">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-272">
                            Ordered
                          </p>
                          <p className="text-lg font-black text-slate-900" data-id="element-273">
                            {scanningItem.quantity}
                          </p>
                        </div>
                        <div className="text-center" data-id="element-274">
                          <p className="text-[10px] font-black text-emerald-400 uppercase tracking-widest" data-id="element-275">
                            Picked
                          </p>
                          <p className="text-lg font-black text-emerald-600" data-id="element-276">
                            {scanningItem.pickedQuantity}
                          </p>
                        </div>
                        <div className="text-center" data-id="element-277">
                          <p className="text-[10px] font-black text-blue-400 uppercase tracking-widest" data-id="element-278">
                            Remaining
                          </p>
                          <p className="text-lg font-black text-blue-600" data-id="element-279">
                            {scanningItem.quantity - scanningItem.pickedQuantity}
                          </p>
                        </div>
                      </div>
                    </div>

                    {scanningItem.lotNumbers && <div className="space-y-3" data-id="element-280">
                        <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-281">
                          Select Lot Number
                        </label>
                        <div className="grid gap-2" data-id="element-282">
                          {scanningItem.lotNumbers.map(lot => <button key={lot.number} onClick={() => setTempLot(lot.number)} className={cn('p-4 rounded-2xl border-2 text-left transition-all flex justify-between items-center', tempLot === lot.number ? 'border-blue-600 bg-blue-50/50' : 'border-slate-100 hover:border-slate-200')} data-id="element-283">
                              <div data-id="element-284">
                                <p className="text-sm font-black text-slate-900" data-id="element-285">
                                  {lot.number}
                                </p>
                                <p className="text-[10px] font-bold text-slate-400 uppercase" data-id="element-286">
                                  EXP: {lot.expiryDate}
                                </p>
                              </div>
                              <div className="text-right" data-id="element-287">
                                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest" data-id="element-288">
                                  Stock
                                </p>
                                <p className="text-sm font-black text-slate-600" data-id="element-289">
                                  {lot.stock}
                                </p>
                              </div>
                            </button>)}
                        </div>
                      </div>}

                    <div className="space-y-3" data-id="element-290">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-291">
                        Confirm Picked Quantity
                      </label>
                      <div className="flex items-center gap-4" data-id="element-292">
                        <button onClick={() => setManualQty(Math.max(0, parseInt(manualQty || '0') - 1).toString())} className="w-14 h-14 rounded-2xl border-2 border-slate-100 flex items-center justify-center text-2xl font-black text-slate-400 hover:bg-slate-50 transition-colors" data-id="element-293">
                          -
                        </button>
                        <div className="flex-1 relative" data-id="element-294">
                          <input type="number" value={manualQty} onChange={e => setManualQty(e.target.value)} className="w-full text-center py-4 bg-slate-50 border-2 border-slate-100 rounded-2xl outline-none font-black text-2xl text-slate-900 focus:border-blue-500 transition-all" data-id="element-295" />
                        </div>
                        <button onClick={() => setManualQty(Math.min(scanningItem.quantity, parseInt(manualQty || '0') + 1).toString())} className="w-14 h-14 rounded-2xl border-2 border-slate-100 flex items-center justify-center text-2xl font-black text-slate-400 hover:bg-slate-50 transition-colors" data-id="element-296">
                          +
                        </button>
                      </div>
                      {parseInt(manualQty) < scanningItem.quantity && <p className="text-[10px] font-bold text-amber-600 flex items-center gap-1.5" data-id="element-297">
                          <AlertCircle className="w-3 h-3" data-id="element-298" />
                          Partial fulfillment:{' '}
                          {scanningItem.quantity - parseInt(manualQty)} units
                          pending
                        </p>}
                    </div>

                    <div className="flex gap-4 pt-4" data-id="element-299">
                      <button onClick={() => {
                  setScanningItem(null);
                  setTempLot('');
                  setManualQty('');
                }} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-300">
                        Back to Scan
                      </button>
                      <button disabled={scanningItem.lotNumbers && scanningItem.lotNumbers.length > 0 && !tempLot} onClick={() => {
                  handleConfirmItemFulfillment();
                }} className="flex-[1.5] py-4 bg-blue-400 text-white font-black rounded-2xl shadow-lg shadow-blue-100 hover:bg-blue-500 transition-all disabled:opacity-50" data-id="element-301">
                        Confirm & Next Scan
                      </button>
                    </div>
                  </div> : <div className="space-y-8" data-id="element-302">
                    <div className="text-center space-y-2" data-id="element-303">
                      <div className="w-20 h-20 bg-blue-50 rounded-3xl flex items-center justify-center mx-auto text-blue-600" data-id="element-304">
                        <Scan className="w-10 h-10" data-id="element-305" />
                      </div>
                      <h4 className="font-bold text-xl text-slate-900" data-id="element-306">
                        Ready to Scan
                      </h4>
                      <p className="text-sm text-slate-500" data-id="element-307">
                        Scan an item's barcode or serial number to begin
                        fulfillment.
                      </p>
                    </div>

                    <div className="space-y-2" data-id="element-308">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-309">
                        Scan Item
                      </label>
                      <div className="relative" data-id="element-310">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2" data-id="element-311">
                          <Scan className="w-5 h-5 text-blue-400" data-id="element-312" />
                        </div>
                        <input autoFocus type="text" value={globalScanValue} onChange={e => setGlobalScanValue(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleGlobalScan(globalScanValue);
                    }
                  }} placeholder="Scan barcode, SKU, or Lot..." className="w-full pl-12 pr-4 py-5 bg-white border-2 border-blue-500 rounded-2xl outline-none shadow-[0_0_0_4px_rgba(59,130,246,0.1)] font-mono font-bold text-slate-700 placeholder:text-slate-300 transition-all" data-id="element-313" />
                      </div>
                    </div>

                    <div className="flex gap-4 pt-4" data-id="element-314">
                      <button onClick={() => {
                  setShowGlobalScanModal(false);
                  setIsBulkScanning(false);
                }} className="w-full py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-315">
                        Cancel & Close
                      </button>
                    </div>
                  </div>}
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Cancel Order Modal */}
      <AnimatePresence data-id="element-316">
        {showRejectModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-317">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-318">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-rose-50/60" data-id="element-319">
                <div className="flex items-center gap-3" data-id="element-320">
                  <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-600 flex items-center justify-center" data-id="element-321">
                    <XCircle className="w-6 h-6" data-id="element-322" />
                  </div>
                  <div data-id="element-323">
                    <h3 className="font-bold text-slate-900" data-id="element-324">Cancel Order</h3>
                    <p className="text-[10px] font-bold text-rose-600 uppercase tracking-widest" data-id="element-325">
                      Reason required
                    </p>
                  </div>
                </div>
                <button onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="p-1 rounded-full hover:bg-white transition-colors" aria-label="Close cancellation dialog" data-id="element-326">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-327" />
                </button>
              </div>

              <div className="p-6 space-y-4" data-id="element-328">
                <p className="text-sm text-slate-600" data-id="element-329">
                  Select why this order is being cancelled. This will be saved
                  in the order history.
                </p>
                <div className="space-y-2" data-id="element-330">
                  {rejectionReasons.map(reason => <button key={reason} onClick={() => {
                setRejectionReason(reason);
                if (reason !== 'Other') setCustomReason('');
              }} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-bold transition-all', rejectionReason === reason ? 'border-rose-600 bg-rose-50 text-rose-700' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-331">
                      {reason}
                    </button>)}
                </div>

                {rejectionReason === 'Other' && <textarea value={customReason} onChange={event => setCustomReason(event.target.value)} placeholder="Enter the cancellation reason..." className="w-full min-h-[88px] p-4 rounded-xl border-2 border-slate-100 bg-slate-50 text-sm outline-none focus:border-rose-500 focus:bg-white transition-colors" data-id="element-332" />}
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-3" data-id="element-333">
                <button onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="flex-1 py-3.5 rounded-xl text-slate-600 font-bold hover:bg-white transition-colors" data-id="element-334">
                  Go Back
                </button>
                <button disabled={!rejectionReason || rejectionReason === 'Other' && !customReason.trim()} onClick={() => {
              const reason = rejectionReason === 'Other' ? customReason.trim() : rejectionReason;
              onUpdateStatus('Cancelled', {
                rejectionReason: reason
              });
              setShowRejectModal(false);
              setRejectionReason('');
              setCustomReason('');
            }} className="flex-1 py-3.5 rounded-xl bg-rose-600 text-white font-bold shadow-lg shadow-rose-100 hover:bg-rose-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-335">
                  Confirm Cancel
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Partial Fulfillment Modal */}
      <AnimatePresence data-id="element-336">
        {showPartialModal && <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-337">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-[32px] w-full max-w-lg overflow-hidden shadow-2xl" data-id="element-338">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-339">
                <h3 className="font-bold text-slate-900" data-id="element-340">
                  Partial Fulfillment
                </h3>
                <button onClick={() => setShowPartialModal(false)} className="p-1 hover:bg-slate-100 rounded-full transition-colors" data-id="element-341">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-342" />
                </button>
              </div>

              <div className="p-8 space-y-8" data-id="element-343">
                <div className="text-center space-y-4" data-id="element-344">
                  <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto text-amber-500" data-id="element-345">
                    <AlertCircle className="w-8 h-8" data-id="element-346" />
                  </div>
                  <div className="space-y-2" data-id="element-347">
                    <h4 className="text-xl font-black text-slate-900" data-id="element-348">
                      Items Still Pending
                    </h4>
                    <p className="text-sm text-slate-500 font-medium max-w-[280px] mx-auto" data-id="element-349">
                      Some items in this order have not been fully scanned or
                      picked.
                    </p>
                  </div>
                </div>

                <div className="space-y-3 max-h-[240px] overflow-auto pr-2 custom-scrollbar" data-id="element-350">
                  {items.filter(i => i.status !== 'Picked' && i.status !== 'Unavailable').map(item => <div key={item.id} className="p-4 bg-slate-50 rounded-2xl border border-slate-100 flex items-center justify-between" data-id="element-351">
                        <div data-id="element-352">
                          <p className="text-sm font-black text-slate-900" data-id="element-353">
                            {item.sku}
                          </p>
                          <p className="text-[10px] font-bold text-slate-400" data-id="element-354">
                            {item.name}
                          </p>
                        </div>
                        <div className="text-right" data-id="element-355">
                          <p className="text-sm font-black text-amber-600" data-id="element-356">
                            {item.pickedQuantity} / {item.quantity}
                          </p>
                          <p className="text-[10px] font-black text-amber-400 uppercase tracking-widest" data-id="element-357">
                            {item.status}
                          </p>
                        </div>
                      </div>)}
                </div>

                <div className="p-4 bg-amber-50 rounded-2xl border border-amber-100" data-id="element-358">
                  <p className="text-[11px] text-amber-700 font-bold leading-relaxed" data-id="element-359">
                    <span className="font-black uppercase tracking-wider mr-1" data-id="element-360">
                      Note:
                    </span>
                    Proceeding will mark the order as partially fulfilled. This
                    will trigger a notification to the customer and the ordering
                    channel.
                  </p>
                </div>

                <div className="flex gap-4" data-id="element-361">
                  <button onClick={() => setShowPartialModal(false)} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-362">
                    Go Back
                  </button>
                  <button type="button" onClick={handleConfirmPartial} className="flex-1 py-4 bg-amber-500 text-white font-black rounded-2xl shadow-lg shadow-amber-100 hover:bg-amber-600 transition-all" data-id="element-363">
                    Confirm Partial
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Customer Confirmation Wait Modal */}
      <AnimatePresence data-id="element-364">
        {isWaitingConfirmation && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" role="alertdialog" aria-modal="true" aria-labelledby="customer-confirmation-title" aria-describedby="customer-confirmation-description" data-id="element-365">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 16
        }} className="w-full max-w-sm overflow-hidden rounded-[28px] bg-white shadow-2xl" data-id="element-366">
              <div className="p-8 text-center" data-id="element-367">
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-amber-50 text-amber-600" data-id="element-368">
                  <Clock className="h-8 w-8" data-id="element-369" />
                </div>
                <h3 id="customer-confirmation-title" className="mt-5 text-xl font-black text-slate-900" data-id="element-370">
                  Waiting for Customer Confirmation
                </h3>
                <p id="customer-confirmation-description" className="mt-2 text-sm leading-6 text-slate-500" data-id="element-371">
                  The partial fulfillment request has been sent. This order will
                  continue automatically in 5 seconds.
                </p>
                <div className="mt-6 h-1.5 overflow-hidden rounded-full bg-amber-100" data-id="element-372">
                  <motion.div initial={{
                width: '0%'
              }} animate={{
                width: '100%'
              }} transition={{
                duration: 5,
                ease: 'linear'
              }} className="h-full rounded-full bg-amber-500" data-id="element-373" />
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Return Receipt Confirmation Modal */}
      <AnimatePresence data-id="element-374">
        {showReturnReceiptModal && <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-375">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-[32px] w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-376">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-emerald-50/50" data-id="element-377">
                <div className="flex items-center gap-3" data-id="element-378">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center text-emerald-600" data-id="element-379">
                    <CheckCircle2 className="w-6 h-6" data-id="element-380" />
                  </div>
                  <div data-id="element-381">
                    <h3 className="font-bold text-slate-900" data-id="element-382">
                      Confirm Return Receipt
                    </h3>
                    <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-widest" data-id="element-383">
                      Verify Items from Driver
                    </p>
                  </div>
                </div>
                <button onClick={() => setShowReturnReceiptModal(false)} className="p-1 hover:bg-slate-100 rounded-full transition-colors" data-id="element-384">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-385" />
                </button>
              </div>

              <div className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar" data-id="element-386">
                <div className="space-y-4" data-id="element-387">
                  {items.map(item => <div key={item.id} className={cn('p-5 rounded-2xl border-2 transition-all space-y-4', returnItemStatus[item.id]?.condition === 'Good' ? 'border-slate-100 bg-slate-50/30' : returnItemStatus[item.id]?.condition === 'Damaged' ? 'border-rose-100 bg-rose-50/30' : 'border-amber-100 bg-amber-50/30')} data-id="element-388">
                      <div className="flex items-center justify-between" data-id="element-389">
                        <div className="flex items-center gap-4" data-id="element-390">
                          <img src={item.image} className="w-12 h-12 rounded-xl object-cover border border-slate-200" alt="" data-id="element-391" />
                          <div data-id="element-392">
                            <p className="text-sm font-black text-slate-900" data-id="element-393">
                              {item.name}
                            </p>
                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider" data-id="element-394">
                              SKU: {item.sku} • Qty: {item.quantity}
                            </p>
                          </div>
                        </div>
                        <div className="flex bg-white rounded-xl p-1 border border-slate-200 shadow-sm" data-id="element-395">
                          {(['Good', 'Damaged', 'Missing'] as const).map(cond => <button key={cond} onClick={() => setReturnItemStatus(prev => ({
                      ...prev,
                      [item.id]: {
                        ...prev[item.id],
                        condition: cond,
                        received: cond !== 'Missing'
                      }
                    }))} className={cn('px-3 py-1.5 rounded-lg text-[10px] font-black uppercase transition-all', returnItemStatus[item.id]?.condition === cond ? cond === 'Good' ? 'bg-emerald-500 text-white shadow-md shadow-emerald-100' : cond === 'Damaged' ? 'bg-rose-500 text-white shadow-md shadow-rose-100' : 'bg-amber-500 text-white shadow-md shadow-amber-100' : 'text-slate-400 hover:bg-slate-50')} data-id="element-396">
                                {cond}
                              </button>)}
                        </div>
                      </div>

                      {returnItemStatus[item.id]?.condition !== 'Good' && <div className="animate-in fade-in slide-in-from-top-2 duration-200 space-y-4" data-id="element-397">
                          {returnItemStatus[item.id]?.condition === 'Missing' && <div className="space-y-2" data-id="element-398">
                              <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest" data-id="element-399">
                                How many units are missing?
                              </label>
                              <div className="flex items-center gap-3" data-id="element-400">
                                <button onClick={() => setReturnItemStatus(prev => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          missingQuantity: Math.max(0, prev[item.id].missingQuantity - 1)
                        }
                      }))} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-lg font-bold text-slate-400 hover:bg-slate-50" data-id="element-401">
                                  -
                                </button>
                                <div className="w-16 text-center font-black text-lg text-slate-900" data-id="element-402">
                                  {returnItemStatus[item.id]?.missingQuantity || 0}
                                </div>
                                <button onClick={() => setReturnItemStatus(prev => ({
                        ...prev,
                        [item.id]: {
                          ...prev[item.id],
                          missingQuantity: Math.min(item.quantity, prev[item.id].missingQuantity + 1)
                        }
                      }))} className="w-10 h-10 rounded-xl border border-slate-200 flex items-center justify-center text-lg font-bold text-slate-400 hover:bg-slate-50" data-id="element-403">
                                  +
                                </button>
                                <span className="text-xs text-slate-400 font-bold" data-id="element-404">
                                  / {item.quantity} units total
                                </span>
                              </div>
                            </div>}
                          <textarea placeholder={`Describe the ${returnItemStatus[item.id]?.condition.toLowerCase()} issue...`} value={returnItemStatus[item.id]?.note} onChange={e => setReturnItemStatus(prev => ({
                    ...prev,
                    [item.id]: {
                      ...prev[item.id],
                      note: e.target.value
                    }
                  }))} className="w-full p-3 bg-white border border-slate-200 rounded-xl text-xs outline-none focus:border-blue-500 transition-all min-h-[60px]" data-id="element-405" />
                        </div>}
                    </div>)}
                </div>
              </div>

              <div className="p-6 bg-slate-50 border-t border-slate-100 flex gap-4" data-id="element-406">
                <button onClick={() => setShowReturnReceiptModal(false)} className="flex-1 py-4 text-slate-500 font-bold hover:bg-slate-50 rounded-2xl transition-colors" data-id="element-407">
                  Cancel
                </button>
                <button onClick={() => {
              onUpdateStatus('Return Requested');
              setShowReturnReceiptModal(false);
              import('sonner').then(({
                toast
              }) => toast.success('Return receipt confirmed. Status updated to Return Requested.'));
            }} className="flex-[2] py-4 bg-emerald-600 text-white font-black rounded-2xl shadow-lg shadow-emerald-100 hover:bg-emerald-700 transition-all" data-id="element-408">
                  Confirm Receipt & Proceed
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Return Modal */}
      <AnimatePresence data-id="element-409">
        {showReturnModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-410">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-411">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-rose-50/50" data-id="element-412">
                <div className="flex items-center gap-3" data-id="element-413">
                  <ArrowLeft className="w-6 h-6 text-rose-600" data-id="element-414" />
                  <h3 className="font-bold text-slate-900" data-id="element-415">Return Process</h3>
                </div>
                <button onClick={() => setShowReturnModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-416">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-417" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-418">
                <div className="grid grid-cols-2 gap-6" data-id="element-419">
                  <div className="space-y-2" data-id="element-420">
                    <label className="text-xs font-bold text-slate-500 uppercase" data-id="element-421">
                      Return Reason
                    </label>
                    <select value={returnReason} onChange={e => setReturnReason(e.target.value)} className="w-full px-4 py-3 bg-slate-50 border-2 border-slate-100 rounded-xl text-sm outline-none" data-id="element-422">
                      <option value="" data-id="element-423">Select a reason...</option>
                      <option value="Damaged" data-id="element-424">Damaged Item</option>
                      <option value="Wrong" data-id="element-425">Wrong Item Received</option>
                      <option value="Expired" data-id="element-426">Expired Product</option>
                      <option value="Not Needed" data-id="element-427">No longer needed</option>
                    </select>
                  </div>
                  <div className="space-y-2" data-id="element-428">
                    <label className="text-xs font-bold text-slate-500 uppercase" data-id="element-429">
                      Return Method
                    </label>
                    <div className="grid grid-cols-2 gap-2" data-id="element-430">
                      <button onClick={() => setReturnMethod('Driver')} className={cn('py-3 rounded-xl border-2 text-xs font-bold transition-all', returnMethod === 'Driver' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 text-slate-500 hover:border-slate-200')} data-id="element-431">
                        Request Driver
                      </button>
                      <button onClick={() => setReturnMethod('Customer')} className={cn('py-3 rounded-xl border-2 text-xs font-bold transition-all', returnMethod === 'Customer' ? 'border-blue-600 bg-blue-50 text-blue-600' : 'border-slate-100 text-slate-500 hover:border-slate-200')} data-id="element-432">
                        Customer Return
                      </button>
                    </div>
                  </div>
                </div>

                <div className="space-y-4" data-id="element-433">
                  {items.map(item => <div key={item.id} className="p-4 bg-slate-50 rounded-xl flex items-center justify-between" data-id="element-434">
                      <div className="flex items-center gap-3" data-id="element-435">
                        <img src={item.image} className="w-10 h-10 rounded-lg" data-id="element-436" />
                        <p className="text-sm font-bold" data-id="element-437">{item.name}</p>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-438">
                        <button className="w-8 h-8 rounded border border-slate-200" data-id="element-439">
                          -
                        </button>
                        <span className="text-sm font-bold" data-id="element-440">0</span>
                        <button className="w-8 h-8 rounded border border-slate-200" data-id="element-441">
                          +
                        </button>
                        <span className="text-xs text-slate-400" data-id="element-442">
                          / {item.quantity}
                        </span>
                      </div>
                    </div>)}
                </div>
                <button disabled={!returnReason} onClick={() => {
              const nextStatus = returnMethod === 'Driver' ? 'Waiting Return Confirmation' : 'Return Requested';
              onUpdateStatus(nextStatus);
              setShowReturnModal(false);
              import('sonner').then(({
                toast
              }) => toast.success(returnMethod === 'Driver' ? 'Driver requested for return. Waiting for item arrival.' : 'Return initiated. Please scan items when received.'));
            }} className="w-full py-4 bg-rose-600 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-443">
                  Confirm Return ({returnMethod})
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Rx Modal */}
      <AnimatePresence data-id="element-444">
        {showRxModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-445">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden shadow-2xl" data-id="element-446">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between bg-purple-50/50" data-id="element-447">
                <h3 className="font-bold text-slate-900" data-id="element-448">
                  Prescription Attachment
                </h3>
                <button onClick={() => setShowRxModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-449">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-450" />
                </button>
              </div>
              <div className="p-8" data-id="element-451">
                <div className="aspect-[4/3] bg-slate-100 rounded-2xl border-2 border-slate-200 overflow-hidden" data-id="element-452">
                  {order.prescriptionImage ? <img src={order.prescriptionImage} alt="Rx" className="w-full h-full object-contain" data-id="element-453" /> : <div className="flex flex-col items-center justify-center h-full text-slate-400" data-id="element-454">
                      <Globe className="w-12 h-12 mb-2" data-id="element-455" />
                      <p className="font-bold" data-id="element-456">No Image Attached</p>
                    </div>}
                </div>
                <div className="mt-6 flex gap-3" data-id="element-457">
                  <button onClick={() => setShowRxModal(false)} className="flex-1 py-4 bg-white text-slate-600 font-bold border border-slate-200 rounded-2xl" data-id="element-458">
                    Close
                  </button>
                  <button onClick={() => {
                setShowRxModal(false);
                setShowHoldModal(true);
                setHoldReason('Prescription Not Valid');
              }} className="flex-1 py-4 bg-amber-500 text-white font-bold rounded-2xl shadow-lg flex items-center justify-center gap-2" data-id="element-459">
                    <Clock className="w-4 h-4" data-id="element-460" />
                    Hold: Invalid Rx
                  </button>
                </div>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* Hold Modal */}
      <AnimatePresence data-id="element-461">
        {showHoldModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-462">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-463">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-464">
                <h3 className="font-bold text-slate-900" data-id="element-465">Hold Order</h3>
                <button onClick={() => setShowHoldModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-466">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-467" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-468">
                <div className="space-y-2" data-id="element-469">
                  {['Customer Unreachable', 'Incorrect Address', 'Prescription Not Valid', 'Other'].map(reason => <button key={reason} onClick={() => setHoldReason(reason)} className={cn('w-full px-4 py-3 rounded-xl border-2 text-left text-sm font-medium transition-all', holdReason === reason ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-100 bg-slate-50 text-slate-600 hover:border-slate-200')} data-id="element-470">
                      {reason}
                    </button>)}
                </div>
                <button disabled={!holdReason} onClick={() => {
              onUpdateStatus('Held', {
                holdReason
              });
              setShowHoldModal(false);
            }} className="w-full py-4 bg-slate-800 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-471">
                  Confirm Hold
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>

      {/* OTP Modal */}
      <AnimatePresence data-id="element-472">
        {showOtpModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm" data-id="element-473">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.95
        }} className="bg-white rounded-3xl w-full max-w-md overflow-hidden shadow-2xl" data-id="element-474">
              <div className="p-6 border-b border-slate-100 flex items-center justify-between" data-id="element-475">
                <h3 className="font-bold text-slate-900" data-id="element-476">OTP Verification</h3>
                <button onClick={() => setShowOtpModal(false)} className="p-1 hover:bg-slate-100 rounded-full" data-id="element-477">
                  <XCircle className="w-5 h-5 text-slate-400" data-id="element-478" />
                </button>
              </div>
              <div className="p-8 space-y-6" data-id="element-479">
                <div className="rounded-2xl border border-purple-100 bg-purple-50 p-4 text-center" data-id="element-480">
                  <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-white text-purple-600 shadow-sm" data-id="element-481">
                    <User className="h-5 w-5" data-id="element-482" />
                  </div>
                  <p className="text-[10px] font-black uppercase tracking-widest text-purple-600" data-id="element-483">
                    Self Pickup
                  </p>
                  <p className="mt-1 text-sm font-bold text-slate-900" data-id="element-484">
                    {order.customerName} is collecting this order
                  </p>
                </div>
                <div className="text-center space-y-2" data-id="element-485">
                  <Smartphone className="w-8 h-8 text-blue-600 mx-auto" data-id="element-486" />
                  <h4 className="font-bold text-lg" data-id="element-487">Confirm Pickup with OTP</h4>
                  <p className="text-sm text-slate-500" data-id="element-488">
                    Ask the customer for the 4-digit collection code sent to
                    {` ${order.customerPhone || ' their phone'}`}.
                  </p>
                </div>
                <input autoFocus inputMode="numeric" type="text" maxLength={4} value={otpValue} onChange={e => setOtpValue(e.target.value.replace(/\D/g, ''))} aria-label="Four digit pickup OTP" className="w-full text-center py-6 bg-slate-50 border-2 border-slate-100 rounded-2xl font-black text-4xl tracking-widest outline-none focus:border-blue-500" data-id="element-489" />
                <button disabled={otpValue.length !== 4} onClick={() => {
              if (otpValue === order.otp) {
                onUpdateStatus('Delivered', {
                  otpVerified: true
                });
                setShowOtpModal(false);
              } else {
                import('sonner').then(({
                  toast
                }) => toast.error('Invalid OTP'));
              }
            }} className="w-full py-4 bg-blue-600 text-white font-bold rounded-2xl shadow-lg disabled:opacity-50" data-id="element-490">
                  Verify & Complete
                </button>
              </div>
            </motion.div>
          </div>}
      </AnimatePresence>
    </div>;
}